import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, useLocation } from "wouter";
import {
  useListOrders,
  useListNotificationChanges,
  getListNotificationChangesQueryKey,
  useCreateDispute,
  useGetMe,
  getListDisputesQueryKey,
  type Order,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { StatusBadge } from "@/components/StatusBadge";
import { formatZAR } from "@/lib/formatters";
import { Search, ChevronRight, ArrowRight, MapPin, Package, Hash, Loader2, AlertTriangle, X, Phone, Mail, Building2, Tag, Calendar, CheckCircle2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { DisputesList } from "@/pages/support/DisputesList";

const FILTERS: { label: string; value: string }[] = [
  { label: "All", value: "all" },
  { label: "Pending", value: "pending" },
  { label: "In progress", value: "in_progress" },
  { label: "Action required", value: "action_required" },
  { label: "Installed", value: "installed" },
  { label: "Active", value: "active" },
  { label: "Cancelled", value: "cancelled" },
];

function prettyLabel(value: string | null | undefined): string {
  if (!value) return "—";
  return value
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-ZA", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function findOrderByDeepLinkParam(
  orders: Order[],
  param: string,
): Order | undefined {
  const key = param.trim();
  return orders.find(
    (o) =>
      String(o.id) === key ||
      o.reference === key ||
      o.externalLeadId === key,
  );
}

export function OrdersList() {
  const { data: ordersPage, isLoading, error } = useListOrders();
  const { data: me } = useGetMe();
  const isAgent = me?.role === "agent";
  const itemNoun = isAgent ? "lead" : "order";
  const itemNounPlural = isAgent ? "leads" : "orders";
  const orders = ordersPage?.items;
  const [searchParams] = useSearchParams();
  const [location, setLocation] = useLocation();
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState<Order | null>(null);
  const [disputeOpen, setDisputeOpen] = useState(false);

  const notificationId = searchParams.get("notification");
  const notificationIdNum = Number(notificationId);
  const notificationEnabled =
    notificationId != null && notificationId !== "" && !Number.isNaN(notificationIdNum);
  const { data: changes } = useListNotificationChanges(notificationIdNum, {
    query: {
      enabled: notificationEnabled,
      queryKey: getListNotificationChangesQueryKey(notificationIdNum),
    },
  });

  // Deep-link: /orders?...&order=<id|reference|externalLeadId> opens that
  // order's detail sheet on load. We apply it once per param so closing the
  // sheet doesn't re-open it.
  const deepLinkOrderId = searchParams.get("order");
  const appliedDeepLinkRef = useRef<string | null>(null);
  useEffect(() => {
    if (!deepLinkOrderId || !orders) return;
    if (appliedDeepLinkRef.current === deepLinkOrderId) return;
    const target = findOrderByDeepLinkParam(orders, deepLinkOrderId);
    if (target) {
      appliedDeepLinkRef.current = deepLinkOrderId;
      setSelected(target);
    }
  }, [deepLinkOrderId, orders]);

  function openOrder(orderId: number) {
    const target = orders?.find((o) => o.id === orderId);
    if (target) setSelected(target);
  }

  // When arriving from a notification, scope the list to the orders that moved
  // so the agent can act on exactly what changed.
  const changedOrderIds = useMemo(
    () => new Set((changes ?? []).map((c) => c.orderId)),
    [changes],
  );
  const scopeToChanges = Boolean(notificationId) && changedOrderIds.size > 0;

  function clearNotificationFilter() {
    const base = location.split("?")[0];
    setLocation(base);
  }

  const filtered = useMemo(() => {
    if (!orders) return [];
    const q = query.trim().toLowerCase();
    return orders.filter((o) => {
      const matchesScope = !scopeToChanges || changedOrderIds.has(o.id);
      const matchesFilter = filter === "all" || o.status === filter;
      const matchesQuery =
        !q ||
        o.customerName.toLowerCase().includes(q) ||
        o.reference.toLowerCase().includes(q) ||
        (o.address ?? "").toLowerCase().includes(q);
      return matchesScope && matchesFilter && matchesQuery;
    });
  }, [orders, query, filter, scopeToChanges, changedOrderIds]);

  if (isLoading)
    return (
      <div className="p-6 animate-pulse">
        <div className="h-8 bg-gray-200 w-1/3 rounded mb-4"></div>
        <div className="h-64 bg-gray-200 rounded"></div>
      </div>
    );
  if (error || !orders)
    return (
      <div className="p-6 text-red-500">Failed to load {itemNounPlural}.</div>
    );

  const leadsBody = (
    <div className="space-y-5">
      {notificationId && changes && changes.length > 0 && (
        <Card className="border-[#E6007E]/20 bg-pink-50/40 rounded-xl shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-bold text-[#0B132B]">
                  {changes.length} {itemNoun}
                  {changes.length === 1 ? "" : "s"} updated in your pipeline
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  Showing only the {itemNounPlural} that changed.
                </p>
              </div>
              <button
                onClick={clearNotificationFilter}
                className="shrink-0 text-xs font-medium text-gray-500 hover:text-[#0B132B] flex items-center gap-1"
              >
                <X className="w-3.5 h-3.5" /> Clear
              </button>
            </div>
            <div className="mt-3 flex flex-col gap-2">
              {changes.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => openOrder(c.orderId)}
                  className="w-full text-left flex items-center justify-between gap-3 rounded-lg bg-white border border-gray-100 px-3 py-2 hover:border-[#E6007E]/30 transition-colors"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-[#0B132B] truncate">
                      {c.customerName ?? "Customer"}
                    </p>
                    <p className="text-[11px] text-gray-400">
                      {c.orderReference ?? `#${c.orderId}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {c.previousStatus && (
                      <>
                        <StatusBadge status={c.previousStatus} />
                        <ArrowRight className="w-3.5 h-3.5 text-gray-300" />
                      </>
                    )}
                    <StatusBadge status={c.newStatus} />
                    <ChevronRight className="w-4 h-4 text-gray-300" />
                  </div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by name, reference or address"
          className="pl-9 bg-white"
        />
      </div>

      <div className="flex gap-2 overflow-x-auto -mx-5 px-5 pb-1 no-scrollbar">
        {FILTERS.map((f) => {
          const active = filter === f.value;
          return (
            <button
              key={f.value}
              onClick={() => setFilter(f.value)}
              className={`shrink-0 rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors border ${
                active
                  ? "bg-[#E6007E] text-white border-[#E6007E]"
                  : "bg-white text-gray-600 border-gray-200 hover:border-gray-300"
              }`}
            >
              {f.label}
            </button>
          );
        })}
      </div>

      <div className="flex flex-col gap-3">
        {filtered.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            No {itemNounPlural} found.
          </div>
        ) : (
          filtered.map((order) => (
            <Card
              key={order.id}
              onClick={() => setSelected(order)}
              className="border-gray-100 shadow-sm rounded-xl cursor-pointer hover:border-[#E6007E]/30 transition-colors"
            >
              <CardContent className="p-4 flex flex-col gap-3">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-semibold text-[15px] text-[#0B132B]">
                      {order.customerName}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {order.address}
                    </p>
                  </div>
                  <StatusBadge status={order.status} />
                </div>
                <div className="h-px w-full bg-gray-50"></div>
                <div className="flex justify-between items-end">
                  <div>
                    <p className="text-xs font-medium text-gray-600 mb-0.5">
                      {order.package}
                    </p>
                    <p className="text-[11px] text-gray-400">
                      {order.reference}
                    </p>
                  </div>
                  <div className="flex items-center gap-1">
                    <p className="font-bold text-sm text-[#0B132B]">
                      {formatZAR(order.priceCents)}
                    </p>
                    <ChevronRight className="w-4 h-4 text-gray-300" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

    </div>
  );

  return (
    <div className="space-y-5">
      <h1 className="text-2xl font-bold text-[#0B132B]">
        {isAgent ? "Leads" : "Orders"}
      </h1>

      {isAgent ? (
        <Tabs defaultValue="leads" className="w-full">
          <TabsList className="bg-gray-100">
            <TabsTrigger value="leads">Leads</TabsTrigger>
            <TabsTrigger value="disputes">Disputes</TabsTrigger>
          </TabsList>
          <TabsContent value="leads" className="mt-4">
            {leadsBody}
          </TabsContent>
          <TabsContent value="disputes" className="mt-4">
            <DisputesList embedded />
          </TabsContent>
        </Tabs>
      ) : (
        leadsBody
      )}

      <OrderDetailSheet
        order={selected}
        onClose={() => setSelected(null)}
        onRaiseDispute={() => setDisputeOpen(true)}
      />

      <RaiseDisputeDialog
        order={selected}
        noun={itemNoun}
        open={disputeOpen}
        onOpenChange={setDisputeOpen}
      />
    </div>
  );
}

function OrderDetailSheet({
  order,
  onClose,
  onRaiseDispute,
}: {
  order: Order | null;
  onClose: () => void;
  onRaiseDispute: () => void;
}) {
  return (
    <Sheet open={Boolean(order)} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="bottom" className="rounded-t-2xl max-w-[480px] mx-auto">
        {order && (
          <>
            <SheetHeader className="text-left">
              <SheetTitle className="text-[#0B132B]">
                {order.customerName}
              </SheetTitle>
            </SheetHeader>
            <div className="mt-4 space-y-4">
              <div className="flex items-center justify-between">
                <StatusBadge status={order.status} />
                <span className="text-lg font-bold text-[#0B132B]">
                  {formatZAR(order.priceCents)}
                </span>
              </div>
              <div className="rounded-xl border border-gray-100 divide-y divide-gray-50">
                <DetailRow icon={Hash} label="Reference" value={order.reference} />
                {order.externalLeadId && (
                  <DetailRow
                    icon={Hash}
                    label="Lead ID"
                    value={order.externalLeadId}
                  />
                )}
                <DetailRow
                  icon={Package}
                  label="Package"
                  value={order.package}
                />
                {order.leadStatus && (
                  <DetailRow
                    icon={Tag}
                    label="Lead status"
                    value={prettyLabel(order.leadStatus)}
                  />
                )}
                {order.leadType && (
                  <DetailRow
                    icon={Tag}
                    label="Lead type"
                    value={prettyLabel(order.leadType)}
                  />
                )}
                <DetailRow
                  icon={MapPin}
                  label="Address"
                  value={order.address ?? "—"}
                />
                {order.region && (
                  <DetailRow icon={MapPin} label="Region" value={order.region} />
                )}
                {order.isp && (
                  <DetailRow icon={Package} label="ISP" value={order.isp} />
                )}
                {order.dealer && (
                  <DetailRow
                    icon={Building2}
                    label="Dealer"
                    value={order.dealer}
                  />
                )}
                {order.contactNumber && (
                  <DetailRow
                    icon={Phone}
                    label="Contact"
                    value={order.contactNumber}
                  />
                )}
                {order.contactEmail && (
                  <DetailRow
                    icon={Mail}
                    label="Email"
                    value={order.contactEmail}
                  />
                )}
                {order.dateCaptured && (
                  <DetailRow
                    icon={Calendar}
                    label="Captured"
                    value={fmtDate(order.dateCaptured)}
                  />
                )}
                {order.installedAt && (
                  <DetailRow
                    icon={CheckCircle2}
                    label="Installed"
                    value={fmtDate(order.installedAt)}
                  />
                )}
                {order.submittedAt && (
                  <DetailRow
                    icon={Hash}
                    label="Submitted"
                    value={fmtDate(order.submittedAt)}
                  />
                )}
              </div>
              <Button
                variant="outline"
                onClick={onRaiseDispute}
                className="w-full rounded-xl py-6 border-[#E6007E]/30 text-[#E6007E] hover:bg-[#E6007E]/5 hover:text-[#E6007E]"
              >
                <AlertTriangle className="w-4 h-4 mr-2" /> Raise a dispute
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

function DetailRow({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
}) {
  return (
    <div className="px-4 py-3 flex items-center gap-3">
      <Icon className="w-4 h-4 text-gray-400 shrink-0" />
      <span className="text-sm text-gray-500 w-24 shrink-0">{label}</span>
      <span className="text-sm font-medium text-[#0B132B] text-right flex-1">
        {value}
      </span>
    </div>
  );
}

function RaiseDisputeDialog({
  order,
  noun,
  open,
  onOpenChange,
}: {
  order: Order | null;
  noun: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const createDispute = useCreateDispute();

  const submit = async () => {
    if (!subject.trim()) return;
    try {
      await createDispute.mutateAsync({
        data: {
          subject: subject.trim(),
          description: description.trim() || undefined,
          orderId: order?.id,
        },
      });
      queryClient.invalidateQueries({ queryKey: getListDisputesQueryKey() });
      toast({
        title: "Dispute raised",
        description: "Our team will review it shortly.",
      });
      setSubject("");
      setDescription("");
      onOpenChange(false);
    } catch {
      toast({
        title: "Could not raise dispute",
        description: "Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[440px] rounded-2xl">
        <DialogHeader>
          <DialogTitle className="text-[#0B132B]">Raise a dispute</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {order && (
            <p className="text-sm text-gray-500">
              About {noun}{" "}
              <span className="font-medium">{order.reference}</span> —{" "}
              {order.customerName}
            </p>
          )}
          <div className="space-y-1.5">
            <Label>Subject</Label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Short summary of the issue"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Details (optional)</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe what went wrong"
              rows={4}
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            onClick={submit}
            disabled={!subject.trim() || createDispute.isPending}
            className="bg-[#E6007E] hover:bg-[#c4006b] text-white rounded-xl w-full"
          >
            {createDispute.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              "Submit dispute"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
